import { Component, input, output, signal } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NzOptionComponent } from "ng-zorro-antd/select";
import { Job } from "../../../models/job";
import { CachedProjectService } from "../../services/cached-project.service";
import { Subject, firstValueFrom } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { ProjectService } from "../../services/project.service";
import { CachedUserService } from "../../services/cached-user.service";
import { SharedModule } from "../../shared.module";
import { AppConfig } from "../../../app.config";

interface ParentJob extends Job {
  job_name: string;
  iterations_completed: number;
}

@Component({
    selector: "app-jobs-form",
    templateUrl: "./jobs-form.component.html",
    styleUrls: ["./jobs-form.component.less"],
    standalone: true,
    imports: [SharedModule],
})
export class JobsFormComponent {
  private destroy$ = new Subject<void>();
  jobsForm: FormGroup;
  private project_id: string;

  projectId = input<string | null>(null);
  jobsAdded = output<void>();

  isLoading = signal(false);
  parentJobs = signal<ParentJob[]>([]);
  availableJobOwners = signal<{ email: string; id: string }[]>([]);

  constructor(
    private fb: FormBuilder,
    private cachedProjectService: CachedProjectService,
    private notificationService: NzNotificationService,
    private projectService: ProjectService,
    private cachedUserService: CachedUserService
  ) {
    this.jobsForm = this.fb.group({
      jobs: this.fb.array([this.createJobEntry()]),
    });
  }

  ngOnInit() {
    this.cachedUserService
      .getUserDetails()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.availableJobOwners.set(res.all_users);
        },
      });

    const explicitId = this.projectId();
    if (explicitId) {
      this.project_id = explicitId;
      this.getParentJobs();
    } else {
      this.cachedProjectService.currentProject
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (project) => {
            if (project) {
              this.project_id = project.id;
              this.getParentJobs();
            }
          },
          error: () => {
            this.notificationService.error("Error", "An unexpected error occured");
          },
        });
    }
  }

  get jobsArray(): FormArray {
    return this.jobsForm.get("jobs") as FormArray;
  }

  createJobEntry(): FormGroup {
    const jobGroup = this.fb.group({
      job_basepath: this.fb.control<string>("", [Validators.required, Validators.minLength(1), Validators.pattern(/^\/.+$/)]),
      x2_is_inline: this.fb.control<boolean>(false),
      parent_job: this.fb.control<ParentJob | null>(null),
      parent_cp: this.fb.control<number>(null),
      parent_max_iteration: this.fb.control<number>(null),
      owner: this.fb.control<any>(null),
      is_aws: this.fb.control<boolean>(AppConfig.settings?.is_aws_default || false)
    });

    jobGroup.controls["parent_cp"].disable();

    jobGroup.controls["parent_job"].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (val: ParentJob | null) => {
          if (val) {
            jobGroup.controls["parent_cp"].setValidators([Validators.required]);
            jobGroup.controls["parent_cp"].enable();
            jobGroup.controls["parent_max_iteration"].setValue(val.iterations_completed);
            jobGroup.controls["parent_cp"].setValue(0);
          } else {
            jobGroup.controls["parent_cp"].clearValidators();
            jobGroup.controls["parent_cp"].disable();
            jobGroup.controls["parent_cp"].setValue(null);
            jobGroup.controls["parent_max_iteration"].setValue(null);
          }
          jobGroup.controls["parent_cp"].updateValueAndValidity({ emitEvent: false });
        },
        error: (err) => {
          console.log(err);
          this.notificationService.error("Error", err);
        },
      });

    return jobGroup;
  }

  addJobEntry(): void {
    this.jobsArray.push(this.createJobEntry());
  }

  removeJobEntry(index: number): void {
    if (this.jobsArray.length > 1) {
      this.jobsArray.removeAt(index);
    }
  }

  getParentMaxIteration(index: number): number | null {
    return this.jobsArray.at(index).get("parent_max_iteration")?.value;
  }

  getParentJobs() {
    firstValueFrom(
      this.cachedProjectService._getProjectJobs(this.project_id, {
        fields: ["id", "job_name", "iterations_completed", "project__name"],
      })
    )
      .then((res) => {
        this.parentJobs.set(
          res.data.sort((a: ParentJob, b: ParentJob) =>
            String(a.job_name).localeCompare(b.job_name)
          )
        );
      })
      .catch((err) => console.log(err));
  }

  searchFn(searchString: string, option: NzOptionComponent) {
    return String(option.nzLabel)
      .toLocaleLowerCase()
      .replace(/_|-/g, " ")
      .includes(searchString.toLocaleLowerCase());
  }

  submitForm() {
    this.jobsArray.controls.forEach((control) => {
      const group = control as FormGroup;
      Object.values(group.controls).forEach((c) => {
        c.markAsDirty();
        c.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });
    });
    if (this.jobsForm.invalid) return;

    const entries = this.jobsArray.controls.map((jobControl) => {
      const jobValue = jobControl.value;
      const parentJob = jobValue.parent_job as ParentJob | null;
      return {
        path: jobValue.job_basepath,
        parent_job_id: parentJob ? parentJob.id : null,
        parent_cp_num: jobValue.parent_cp === null ? null : jobValue.parent_cp,
        x2_is_inline: jobValue.x2_is_inline,
        is_update: false,
        owner: jobValue.owner?.id ?? null,
        is_aws: jobValue.is_aws
      };
    });

    this.isLoading.set(true);
    this.projectService
      .createJobs(this.project_id, entries)
      .pipe(take(1))
      .subscribe({
        next: (res: { failed: Array<any>; passed: Array<any> }) => {
          res["failed"].forEach((element) => {
            this.notificationService.error(
              "Error",
              `Job with path: <div>${element["path"]} </div> <br> failed to be added to the project: <br><b>${element["error"]}</b>`
            );
          });

          res["passed"].forEach((element) => {
            const warnings: string[] = element["warnings"] || [];
            warnings.forEach((warning) => {
              this.notificationService.warning("Warning", warning);
            });
            // show link to the job also
            const jobLink = `/projects/${element["job"]['id']}`;
            this.notificationService.success(
              "Success",
              `Job <div><b>${element["path"]}</b></div> was successfully added to the project\n Click <a target="_blank" href="${jobLink}">here</a> to view the job.`
            );
          });

          if (res["passed"].length > 0) {
            this.jobsArray.clear();
            this.jobsArray.push(this.createJobEntry());
            this.getParentJobs();
            this.jobsAdded.emit();
          }

          this.isLoading.set(false);
        },
        error: (err) => {
          this.notificationService.error(
            "Error",
            err.error?.error || "An unexpected error occured"
          );
          this.isLoading.set(false);
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
